<template>
  <div class="qa-item-thread">
    <QuestionAnswerItemThreadNavigation :mnemo="mnemo" :question="question" />

    <AppCard>
      <v-card-text class="qa-item-thread__content">
        <QuestionAnswerItemThreadQuestion :mnemo="mnemo" />

        <div v-if="hasAnswers" class="qa-item-thread__answers">
          <div class="qa-item-thread__answers-title heading-sm-bold">
            {{ $t('project.question-answer.QuestionAnswerItemThread.answers', { nbAnswers: answersCount }) }}
          </div>

          <v-timeline align="start" density="compact">
            <QuestionAnswerItemThreadAnswer
              v-for="answer in question.answers"
              :key="`answer-${answer.id}`"
              :answer="answer"
              :mentionables="mentionables"
            />
          </v-timeline>
        </div>

        <div class="qa-item-thread__add-answer">
          <QuestionAnswerItemThreadAnswerAdd
            v-if="canAddAnswer"
            ref="answerAddForm"
            :mnemo="mnemo"
          />
        </div>
      </v-card-text>
    </AppCard>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import { toast } from '@/common/utils/toast'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'

import QuestionAnswerItemThreadAnswer from './QuestionAnswerItemThreadAnswer.vue'
import QuestionAnswerItemThreadAnswerAdd from './QuestionAnswerItemThreadAnswerAdd.vue'
import QuestionAnswerItemThreadNavigation from './QuestionAnswerItemThreadNavigation.vue'
import QuestionAnswerItemThreadQuestion from './QuestionAnswerItemThreadQuestion.vue'

export default {
  name: 'QuestionAnswerItemThread',
  components: {
    AppCard,
    QuestionAnswerItemThreadAnswer,
    QuestionAnswerItemThreadAnswerAdd,
    QuestionAnswerItemThreadNavigation,
    QuestionAnswerItemThreadQuestion,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState('questionAnswer', [
      'question',
      'markQuestionAsResolvedPending',
      'questionParticipants',
    ]),
    ...mapState('room', ['currentRoom']),
    answersCount () {
      return Array.isArray(this.question?.answers) ? this.question.answers.length : 0
    },
    hasAnswers () {
      return this.answersCount > 0
    },
    canAddAnswer () {
      return this.question?.status !== 'validated'
    },
    mentionables () {
      const deduplicated = new Map()
      const author = this.questionParticipants.author
      if (author?.id) {
        deduplicated.set(author.id, author)
      }
      this.questionParticipants.users.forEach((user) => {
        if (user?.id) {
          deduplicated.set(user.id, user)
        }
      })
      return Array.from(deduplicated.values()).map((user) => ({
        ...user,
        mentionId: `user-${user.id}`,
      }))
    },
  },
  watch: {
    'question.id': {
      immediate: true,
      async handler (questionId) {
        if (!questionId) return
        try {
          await this.GET_QUESTION_PARTICIPANTS({
            mnemo: this.mnemo,
            questionId,
          })
        } catch (error) {
          console.error('Failed to fetch question participants', error)
          toast.error(this.$t('project.question-answer.QuestionAnswerItemThreadAnswerAdd.getParticipantsError'))
        }
      },
    },
  },
  methods: {
    ...mapActions('questionAnswer', [
      QuestionAnswerActionTypes.GET_QUESTION_PARTICIPANTS,
    ]),
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";
@import "@/scss/border-tokens";

.qa-item-thread {
  &__content {
    @media #{map-get($display-breakpoints, 'md-and-up')} {
      padding-left: 90px;
    }
  }

  &__navigation {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 18px;
  }

  &__answers {
    color: rgb(var(--v-theme-text-primary));
    margin-top: 16px;

    &-title {
      margin-bottom: 24px;
      padding-bottom: 10px;
      border-bottom: get-border('border-secondary');
    }
  }

  &__actions {
    margin-top: 20px;
    text-align: right;

    button:not(:last-child) {
      margin-right: 16px;
    }
  }

  &__add-answer {
    margin-top: 24px;
  }

  .v-timeline:before {
    display: none;
  }
}
</style>
